import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useOutletContext } from 'react-router-dom';

import Button from '@components/Button';
import Dialog from '@components/Dialog';
import Toast from '@components/Toast/Toast';

import { datadogLogger } from '@helpers/datadogLogger';

import NoData from '@icons/no_data_found.svg';

import { useDeleteTraveller, useGetTravellers } from '@hooks/useTravellersApi';
import useUserStateSync from '@hooks/useUserStateSync';

import { getTranslationsState } from '@redux/selectors';

import { Traveller } from '@wegoTypes/traveller';

import Skeleton from './components/Skeleton/Skeleton';
import TravellerCard from './components/TravellerCard/TravellerCard';
import TravellerFormView from './components/TravellerFormView/TravellerFormView';
import useTravellersTrackingActions from './hooks/useTravellersTrackingActions';
import styles from './Travellers.module.scss';

const Travellers = () => {
  const { pageViewId } = useOutletContext<{ pageViewId: string }>();
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [showRemoveDialog, setShowRemoveDialog] = useState<boolean>(false);
  const [showFormView, setShowFormView] = useState<boolean>(false);
  const [editTraveller, setEditTraveller] = useState<Traveller | undefined>();
  const [removeTraveller, setRemoveTraveller] = useState<Traveller | undefined>();
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const getTravellerListQuery = useGetTravellers(true);
  const { data: travellerList, isLoading } = getTravellerListQuery;
  const { mutateAsync: deleteTraveller } = useDeleteTraveller();
  const translations = useSelector(getTranslationsState);

  const { refreshUserData } = useUserStateSync();

  const { trackClickAddNewTraveller, trackClickRemoveTraveller } =
    useTravellersTrackingActions(pageViewId);

  const [primaryTraveller, additionalTravellers] = useMemo(() => {
    let primaryTraveller: Traveller | undefined;
    let additionalTravellers: Traveller[] = [];
    if (travellerList) {
      primaryTraveller = travellerList?.list.find((traveller) => traveller.primary);
      additionalTravellers = travellerList?.list.filter((traveller) => !traveller.primary) || [];
    }
    return [primaryTraveller, additionalTravellers];
  }, [travellerList]);

  const onClickEditTraveller = (traveller?: Traveller) => {
    setEditTraveller(traveller);
    setShowFormView(true);
  };

  const onClickAddNewTraveller = () => {
    trackClickAddNewTraveller();
    setEditTraveller(undefined);
    setShowFormView(true);
  };

  const onClickRemoveTraveller = (traveller: Traveller) => {
    setRemoveTraveller(traveller);
    setShowRemoveDialog(true);
  };

  const onCloseForm = (refreshUser?: boolean) => {
    if (refreshUser) {
      refreshUserData();
    }
    setEditTraveller(undefined);
    setShowFormView(false);
  };

  const displayMessage = (message: string) => {
    setToastMessage(message);
  };

  const handleDeleteTraveller = async () => {
    if (!removeTraveller) return;
    trackClickRemoveTraveller();
    try {
      setIsDeleting(true);
      await deleteTraveller(removeTraveller?.id);
      displayMessage(translations.traveller_deleted);
    } catch (error) {
      displayMessage(translations.something_wrong_try_again);
      datadogLogger(error as Error, {
        description: 'Traveller Deletion Failed',
        errorSource: 'Travellers',
        states: {
          traveller: removeTraveller,
        },
      });
    } finally {
      setIsDeleting(false);
      setShowRemoveDialog(false);
      setRemoveTraveller(undefined);
    }
  };

  return isLoading ? (
    <div data-testid='travellers-skeleton' className={styles.loadingContainer}>
      <Skeleton />
    </div>
  ) : (
    <>
      {showFormView ? (
        <TravellerFormView
          pageViewId={pageViewId}
          displayMessage={displayMessage}
          traveller={editTraveller}
          onClose={onCloseForm}
        />
      ) : (
        <div className={styles.container}>
          {!primaryTraveller ? (
            <NoDataFound text={translations.no_traveller_description} />
          ) : (
            <>
              <div className={styles.heading}>{translations.main_traveller}</div>
              <TravellerCard traveller={primaryTraveller} />
              <div className={styles.divider}></div>
              {additionalTravellers.length > 0 && (
                <>
                  <div className={styles.heading}>{translations.other_travellers}</div>
                  <div className={styles.additionalTravellers}>
                    {additionalTravellers.map((traveler) => (
                      <TravellerCard
                        key={traveler.id}
                        traveller={traveler}
                        onEdit={() => onClickEditTraveller(traveler)}
                        onRemove={() => onClickRemoveTraveller(traveler)}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          <div role='button' className={styles.addTravellerBtn} onClick={onClickAddNewTraveller}>
            <div className={styles.btnText}>{translations.add_new_traveller}</div>
          </div>
          <Dialog isOpen={showRemoveDialog} onClose={() => setShowRemoveDialog(false)}>
            <div className={styles.dialog}>
              <div className={styles.heading}>{translations.traveller_delete_confirmation}</div>
              <div className={styles.description}>{translations.traveller_delete_warning}</div>
              <div className={styles.actions}>
                <Button
                  className={styles.actionBtn}
                  colorScheme='grey'
                  size='md'
                  variant='outline'
                  onClick={() => setShowRemoveDialog(false)}
                >
                  {translations.cancel}
                </Button>
                <Button
                  disabled={isDeleting}
                  className={styles.actionBtn}
                  size='md'
                  onClick={handleDeleteTraveller}
                >
                  {translations.delete}
                </Button>
              </div>
            </div>
          </Dialog>
        </div>
      )}
      <Toast message={toastMessage} setMessage={setToastMessage} />
    </>
  );
};

const NoDataFound = ({ text }: { text: string }) => {
  return (
    <div className={styles.noDataContainer}>
      <NoData />
      <div className={styles.title}>{text}</div>
    </div>
  );
};

export default Travellers;
